import { createBrowserRouter, Outlet } from 'react-router-dom';
import PageMapper from '@/lib/PageMapper';
import paths from '@/routes/paths';
import { AuthPostSignOut, AuthSignIn, AuthSignOut, SignIn, SignOut } from '@/auth/AuthControl';
import { AuthSilentRenew } from '@/auth/AuthSilentRenew';
import ProductLinkResolver from '@/pages/ProductLinkResolver';
import ProductContentResolver from '@/pages/ProductContentResolver';
import AnimationStage from '@/lib/AnimationStage';
import { useSelector } from 'react-redux';
import { RootState } from '@/redux/store';
import { PLIPromoTracker } from '@/lib/PLIPromoTracker';
import Loader from '@/pages/Loader';
import ProductLinkAuthResolver from '@/pages/ProductLinkAuthResolver';
import ExternalFileThunk from '@/components/ExternalFileThunk';
import ClaimCreditLinkResolver from '@/pages/ClaimCreditLinkResolver';
import CreditPlanFeedbackResolver from '@/components/ui/CreditPlan/CreditPlanFeedbackResolver';
import CartPanel from '@/components/ui/CheckoutAndOrderManagement/CartPanel';
import EnhancedRouteTracker from '@/components/analytics/EnhancedRouteTracker';
import GlobalProvidersLayout from '@/contexts';
import ClearHelmetSeoOnNavigation from '@/routes/ClearHelmetSeoOnNavigation';

// Create a wrapper that will hold the EnhancedRouteTracker at the app level
// This ensures only one instance is active at a time
const AppLayout = () => {
  return (
    <>
      <ClearHelmetSeoOnNavigation />
      <Outlet />
      <EnhancedRouteTracker />
    </>
  );
};

const BrowserRouter = () => {
  const authState = useSelector((state: RootState) => state.auth);

  return createBrowserRouter([
    {
      element: <GlobalProvidersLayout />,
      children: [
        // External routes that don't need the main layout or tracking
        { path: paths.MARKETING, element: <ExternalFileThunk /> },
        { path: paths.API_ROUTE, element: <ExternalFileThunk /> },
        { path: paths.GLOBAL_ASSETS_ROUTE, element: <ExternalFileThunk /> },
        { path: paths.RSS_ROUTE, element: <ExternalFileThunk /> },
        { path: paths.SITEMAP_ROUTE, element: <ExternalFileThunk /> },
        { path: paths.ROBOTS_ROUTE, element: <ExternalFileThunk /> },

        // Auth routes that don't need the main layout or tracking
        { path: paths.OIDC_SIGNIN, element: <AuthSignIn /> },
        { path: paths.SIGNIN, element: <SignIn /> },
        { path: paths.SIGNOUT, element: <SignOut /> },
        { path: paths.OIDC_SILENT_RENEW, element: <AuthSilentRenew /> },
        { path: paths.OIDC_POST_SIGNOUT_CALLBACK, element: <AuthPostSignOut /> },
        { path: paths.OIDC_SIGNOUT_CALLBACK, element: <AuthSignOut /> },

        // Main application routes that use the AppLayout wrapper for central route tracking
        {
          element: <AppLayout />, // Use the AppLayout wrapper for a single EnhancedRouteTracker
          children: [
            {
              path: paths.CMS_ROUTE,
              element: (
                <AnimationStage>
                  <PageMapper />
                  <PLIPromoTracker />
                  {authState.ready && authState.token && <CartPanel />}
                </AnimationStage>
              )
            },
            {
              path: paths.PRODUCT_REDIRECT,
              element: (
                <AnimationStage>
                  <ProductLinkResolver />
                  <PLIPromoTracker />
                </AnimationStage>
              )
            },
            {
              path: paths.PRODUCT_REDIRECT_AUTH,
              element: (
                <AnimationStage>
                  <ProductLinkAuthResolver />
                  <PLIPromoTracker />
                </AnimationStage>
              )
            },
            {
              path: paths.PLUS_CONTENT_REDIRECT_AUTH,
              element: (
                <AnimationStage>
                  <ProductContentResolver />
                  <PLIPromoTracker />
                </AnimationStage>
              )
            },
            {
              path: paths.CLAIM_CREDIT_REDIRECT,
              element: (
                <AnimationStage>
                  <ClaimCreditLinkResolver />
                  <PLIPromoTracker />
                </AnimationStage>
              )
            },
            {
              path: paths.CREDIT_PLAN_FEEDBACK,
              element: (
                <AnimationStage>
                  <CreditPlanFeedbackResolver />
                  <PLIPromoTracker />
                </AnimationStage>
              )
            },
            {
              path: paths.LAUNCHING,
              element: (
                <AnimationStage>
                  <Loader />
                </AnimationStage>
              )
            }
          ]
        }
      ]
    }
  ]);
};

export default BrowserRouter;
